"use strict";
// The Account Hub's four shared placeholder states. Every future screen
// (Overview/Orders/Profile/Settings) renders through these instead of
// inventing its own loading/error/empty markup — kept as plain functions
// returning an HTML string, matching flix-cart-render.js's split between
// "decide what happened" (flix-cart.js) and "produce markup for it"
// (flix-cart-render.js). No CSS classes: inline style="" from var(--token)
// only, same as the rest of the theme — nothing here is dark/light- or
// RTL/LTR-specific, it all comes for free from the existing tokens and the
// logical (inset-inline-*) properties already used elsewhere.
(function () {
    function escapeHtml(value) {
        var div = document.createElement("div");
        div.textContent = value;
        return div.innerHTML;
    }
    function loadingState(label) {
        return ('<div class="flx-hub-state flx-hub-state--loading" role="status" aria-live="polite" style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;padding:clamp(40px,8vw,72px) 20px;color:var(--dim)">' +
            '<span style="width:34px;height:34px;border-radius:50%;border:3px solid var(--border);border-top-color:var(--accent);animation:flx-hub-spin .8s linear infinite"></span>' +
            '<span style="font-size:13px">' +
            escapeHtml(label) +
            "</span>" +
            "</div>");
    }
    function errorState(input) {
        var retryButton = input.retryLabel
            ? '<button type="button" data-flix-hub-retry style="margin-top:6px;padding:10px 22px;border-radius:var(--radius-button);border:1px solid var(--border);background:transparent;color:var(--text);font-family:var(--display);font-weight:700;font-size:13px;cursor:pointer" style-hover="border-color:var(--borderHi)">' +
                escapeHtml(input.retryLabel) +
                "</button>"
            : "";
        return ('<div class="flx-hub-state flx-hub-state--error" role="alert" style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;text-align:center;padding:clamp(40px,8vw,72px) 24px;color:var(--dim)">' +
            '<span style="width:48px;height:48px;border-radius:50%;background:rgba(255,90,90,.12);border:1px solid rgba(255,90,90,.4);display:flex;align-items:center;justify-content:center;color:var(--error)">!</span>' +
            '<div style="font-family:var(--display);font-weight:800;font-size:15px;color:var(--text)">' +
            escapeHtml(input.title) +
            "</div>" +
            '<div style="font-size:13px;line-height:1.6;max-width:320px">' +
            escapeHtml(input.message) +
            "</div>" +
            retryButton +
            "</div>");
    }
    /**
     * `action` covers the Design System's canonical empty-state formula's
     * gradient CTA (icon-in-circle -> title -> subtext -> CTA) — either a
     * real navigation (`href`) or a data-attribute a delegated click
     * listener elsewhere picks up (`dataAction`, injected as a raw
     * attribute — e.g. "data-flix-orders-claim"; always a fixed string this
     * codebase's own callers supply, never external input).
     */
    function emptyState(input) {
        var icon = input.iconSvg
            ? '<span aria-hidden="true" style="width:72px;height:72px;border-radius:50%;background:var(--surface);border:1px solid var(--border);display:flex;align-items:center;justify-content:center;color:var(--faint)">' +
                input.iconSvg +
                "</span>"
            : "";
        var actionHtml = "";
        if (input.action) {
            var ctaStyle = "margin-top:6px;padding:12px 26px;border-radius:var(--radius-button);border:none;background:linear-gradient(135deg,var(--accent),var(--accent2));color:#160d05;font-family:var(--display);font-weight:800;font-size:14px;text-decoration:none;cursor:pointer;box-shadow:0 8px 22px -8px var(--glow)";
            actionHtml =
                "href" in input.action
                    ? '<a href="' +
                        escapeHtml(input.action.href) +
                        '" style="' +
                        ctaStyle +
                        '" style-hover="transform:translateY(-2px)">' +
                        escapeHtml(input.action.label) +
                        "</a>"
                    : "<button type=\"button\" " +
                        input.action.dataAction +
                        ' style="' +
                        ctaStyle +
                        '" style-hover="transform:translateY(-2px)">' +
                        escapeHtml(input.action.label) +
                        "</button>";
        }
        return ('<div class="flx-hub-state flx-hub-state--empty" style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;text-align:center;padding:clamp(40px,8vw,72px) 24px;color:var(--dim)">' +
            icon +
            '<div style="font-family:var(--display);font-weight:800;font-size:15px;color:var(--text)">' +
            escapeHtml(input.title) +
            "</div>" +
            '<div style="font-size:13px;line-height:1.6;max-width:280px">' +
            escapeHtml(input.message) +
            "</div>" +
            actionHtml +
            "</div>");
    }
    function successState(input) {
        // Mirrors sections/order-details.liquid's existing success-banner
        // pattern (checkmark in a green circle + title + subtitle) rather than
        // the flix:toast mechanism — that event is hardcoded to cart add/remove
        // copy (see assets/flix-toast.js) and even silently ignores type:
        // "error", so it isn't a generic success/error channel to reuse here.
        return ('<div class="flx-hub-state flx-hub-state--success" style="display:flex;align-items:center;gap:14px;padding:16px 20px;border-radius:var(--radius-md);background:color-mix(in srgb, var(--success) 10%, transparent);border:1px solid color-mix(in srgb, var(--success) 40%, transparent)">' +
            '<span style="flex:none;width:42px;height:42px;border-radius:12px;background:linear-gradient(135deg,var(--success),color-mix(in srgb, var(--success) 62%, #ffffff));display:flex;align-items:center;justify-content:center;color:color-mix(in srgb, var(--success) 22%, #000000)">' +
            '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>' +
            "</span>" +
            '<div><div style="font-family:var(--display);font-weight:800;font-size:15px;color:var(--text)">' +
            escapeHtml(input.title) +
            '</div><div style="font-size:13px;color:var(--dim);margin-top:2px">' +
            escapeHtml(input.message) +
            "</div></div>" +
            "</div>");
    }
    window.FlixHubRender = {
        loadingState: loadingState,
        errorState: errorState,
        emptyState: emptyState,
        successState: successState,
    };
})();
